import { FieldDatePicker, FieldInput } from '@isbj-redesign/final-form-components'
import { Alert, Checkbox, Col, Color, Row, Table } from '@isbj-redesign/frontend-components'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { QuerySeitenProps, useQueryDokumente } from './Api'
import { UncontrolledCollapsablePanel } from './CollapsablePanel'
import classes from './Dokumente.module.css'
import DokumentTableRow from './DokumentTableRow'
import { SammelanfrageFooter } from './SammelanfrageFooter'
import { TableFilter } from './TableFilter'
import { TablePagination } from './TablePagination'
import SortableTableHeader, { useSort } from './TableSortableHeader'
import { Dokument } from './types'

const refetchInterval = 10000

export default function DokumenteTable() {
    const [parameter, load] = useState<QuerySeitenProps>({ anzahl: 20 })
    const {
        parameter: sortParameter,
        sort,
        setSort,
    } = useSort<string>([
        { field: 'status', direction: 'asc' },
        { field: 'name', direction: 'asc' },
    ])
    const { isLoading, data } = useQueryDokumente({ ...parameter, sort: sortParameter }, { refetchInterval })
    const { selected, toggle, remove, reset } = useSelection<Dokument>()
    const selectableDokumente = useMemo(() => data?.eintraege.filter((dok) => !dok.anfrage) || [], [data])
    const isSelectAllChecked = useMemo(
        () => selectableDokumente.length > 0 && selectableDokumente.every((dok) => selected.some((it) => it.id === dok.id)),
        [selected, selectableDokumente]
    )
    const onPagination = useCallback((offset, limit) => load((old) => ({ ...old, start: offset, anzahl: limit })), [load])
    useEffect(reset, [parameter, reset])

    return (
        <UncontrolledCollapsablePanel title="Verfügbare Dokumente" loading={isLoading}>
            <TableFilter onSubmit={load}>
                <Row>
                    <Col size={{ xl: 4, lg: 6, xs: 12 }}>
                        <FieldInput name="name" label="Name" />
                    </Col>
                    <Col size={{ xl: 4, lg: 6, xs: 12 }}>
                        <FieldInput name="fachverfahren" label="Fachverfahren" />
                    </Col>
                    <Col size={{ xl: 4, lg: 6, xs: 12 }}>
                        <FieldDatePicker name="erstelltAm" label="Erstellung" />
                    </Col>
                </Row>
            </TableFilter>
            {data && data.eintraege.length ? (
                <div className={classes.table}>
                    <Table caption="Verfügbare Dokumente">
                        <thead>
                            <tr>
                                <th className={classes.toggle}>
                                    <Checkbox
                                        label="Alle Einträg auswählen"
                                        onChange={() => toggle(selectableDokumente)}
                                        checked={isSelectAllChecked}
                                        hideLabel={true}
                                    />
                                </th>
                                <SortableTableHeader field="status" sort={sort} setSort={setSort}>
                                    Status
                                </SortableTableHeader>
                                <SortableTableHeader field="name" sort={sort} setSort={setSort}>
                                    Dokument
                                </SortableTableHeader>
                                <SortableTableHeader field="fachverfahren" sort={sort} setSort={setSort}>
                                    Fachverfahren
                                </SortableTableHeader>
                                <SortableTableHeader field="erstelltAm" sort={sort} setSort={setSort}>
                                    Erstellung
                                </SortableTableHeader>
                                <SortableTableHeader field="gueltigBis" sort={sort} setSort={setSort}>
                                    Gültigkeit
                                </SortableTableHeader>
                                <th>Downloads</th>
                                <th>Letzter Download</th>
                                <th style={{ minWidth: 160 }}>Bereitstellung</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.eintraege.map((dok) => (
                                <DokumentTableRow key={dok.id} dokument={dok} selected={selected} toggle={toggle} remove={remove} />
                            ))}
                        </tbody>
                    </Table>
                    <TablePagination onPagination={onPagination} offset={data.start || 0} limit={data.anzahl || 0} total={data.gesamt || 0} />
                </div>
            ) : (
                <Alert color={Color.Info}>{parameter.suche ? 'Zu Ihrer Suchanfrage wurden keine Ergebnisse gefunden.' : 'Keine Dokumente vorhanden.'}</Alert>
            )}
            <SammelanfrageFooter selected={selected} reset={reset} />
        </UncontrolledCollapsablePanel>
    )
}

function useSelection<Entity extends { id: string }>() {
    const [selected, setSelected] = useState<Entity[]>([])

    const toggle = useCallback((dokumente: Entity[]) => {
        const dokumentenIds = dokumente.map((it) => it.id)
        setSelected((current) =>
            dokumentenIds.every((it) => current.some((d) => d.id === it)) ? current.filter((it) => !dokumentenIds.includes(it.id)) : [...current, ...dokumente]
        )
    }, [])

    const remove = useCallback((dokumente: Entity[]) => {
        const dokumentenIds = dokumente.map((it) => it.id)
        setSelected((current) => current.filter((it) => !dokumentenIds.includes(it.id)))
    }, [])

    const reset = useCallback(() => setSelected([]), [])

    return useMemo(() => ({ selected, toggle, remove, reset }), [selected, toggle, remove, reset])
}
